import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useAuth } from "./AuthProvider";
import { credentialToJson, passkeysPossible, toWebAuthnRequestOptions, type CognitoApi } from "./cognito-api";
import { useCognitoApi } from "./useCognitoApi";
import { Button, Dialog, ErrorState, TextField } from "../components/ui";

/**
 * Step-up (SEC-05; the auth template's StepUpProvider). Not a timer:
 * before a decision of record or a security change, the person must have
 * proved who they are in the last fifteen minutes. If the session's
 * auth_time is older, this asks once — by passkey when they have one,
 * else password and authenticator code — through Cognito's choice flow,
 * and swaps the fresh tokens into the session. The API checks the same
 * claim (requireFreshAuth), so this is the polite half, not the only gate.
 *
 * A federated user has no password or code here: their proof is a fresh
 * round trip through their provider, which brings them back with a new
 * auth_time.
 */
export const STEP_UP_MAX_AGE_S = 15 * 60;

interface StepUpState {
  /** Resolves when the session is fresh enough; rejects if the person cancels. */
  ensureFresh: () => Promise<void>;
  isFresh: () => boolean;
}

const StepUpContext = createContext<StepUpState | undefined>(undefined);

export function StepUpProvider({ children }: { children: ReactNode }) {
  const { session, config, me, replaceTokens, signIn } = useAuth();
  const [open, setOpen] = useState(false);
  const pending = useRef<{ resolve: () => void; reject: (e: unknown) => void }>(undefined);

  const isFresh = useCallback(() => Boolean(session?.authTime && Date.now() / 1000 - session.authTime < STEP_UP_MAX_AGE_S), [session]);
  const ensureFresh = useCallback(() => {
    if (isFresh()) return Promise.resolve();
    return new Promise<void>((resolve, reject) => {
      pending.current = { resolve, reject };
      setOpen(true);
    });
  }, [isFresh]);

  const done = useCallback((ok: boolean, err?: unknown) => {
    setOpen(false);
    const p = pending.current;
    pending.current = undefined;
    if (ok) p?.resolve();
    else p?.reject(err ?? new Error("Confirmation cancelled"));
  }, []);

  const value = useMemo(() => ({ ensureFresh, isFresh }), [ensureFresh, isFresh]);
  return (
    <StepUpContext.Provider value={value}>
      {children}
      {open && session && me?.identityProvider && <FederatedStepUp provider={me.identityProvider} onDone={done} signIn={signIn} />}
      {open && session && !me?.identityProvider && <StepUpDialog config={config} username={session.username ?? session.email ?? ""} accessToken={session.accessToken} onDone={done} replaceTokens={replaceTokens} />}
    </StepUpContext.Provider>
  );
}

export function useStepUp(): StepUpState {
  const ctx = useContext(StepUpContext);
  if (!ctx) throw new Error("useStepUp outside StepUpProvider");
  return ctx;
}

function FederatedStepUp({ provider, onDone, signIn }: { provider: string; onDone: (ok: boolean, err?: unknown) => void; signIn: ReturnType<typeof useAuth>["signIn"] }) {
  const [busy, setBusy] = useState(false);
  return (
    <Dialog title="Confirm it's you" onClose={() => onDone(false)}>
      <p className="text-muted">
        This changes something security-sensitive, and your last sign-in was more than fifteen minutes ago. You sign in through <strong className="text-ink">{provider}</strong>, so we will send you there and bring you straight back here; then try again.
      </p>
      <Button
        variant="primary"
        className="mt-4"
        disabled={busy}
        onClick={() => {
          setBusy(true);
          void signIn(window.location.pathname + window.location.search, { identityProvider: provider });
        }}
      >
        Continue to {provider}
      </Button>
    </Dialog>
  );
}

function StepUpDialog({ config, username, accessToken, onDone, replaceTokens }: { config: ReturnType<typeof useAuth>["config"]; username: string; accessToken: string | undefined; onDone: (ok: boolean, err?: unknown) => void; replaceTokens: ReturnType<typeof useAuth>["replaceTokens"] }) {
  const api: CognitoApi = useCognitoApi(config);
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [challenge, setChallenge] = useState<{ name: string; session: string }>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>();
  // Offer a passkey only when the person has one (pitfall 4).
  const [hasPasskey, setHasPasskey] = useState(false);
  const possible = passkeysPossible();
  useEffect(() => {
    if (!possible || !accessToken) return;
    let live = true;
    api
      .listPasskeys(accessToken)
      .then((r) => live && setHasPasskey((r.Credentials ?? []).length > 0))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [api, accessToken, possible]);

  const finish = async (result: { AuthenticationResult?: Parameters<typeof replaceTokens>[0] }) => {
    if (!result.AuthenticationResult) throw new Error("Cognito answered without tokens");
    await replaceTokens(result.AuthenticationResult);
    onDone(true);
  };

  const withPasskey = async () => {
    setBusy(true);
    try {
      const start = await api.initiateAuth(username, "WEB_AUTHN");
      if (start.ChallengeName !== "WEB_AUTHN" || !start.Session) throw new Error("Cognito did not offer a passkey challenge; use your password and code instead.");
      const options = toWebAuthnRequestOptions(JSON.parse(start.ChallengeParameters?.["CREDENTIAL_REQUEST_OPTIONS"] ?? "{}"));
      const assertion = await navigator.credentials.get(options);
      const result = await api.respondToChallenge("WEB_AUTHN", start.Session, { USERNAME: username, CREDENTIAL: JSON.stringify(credentialToJson(assertion)) });
      await finish(result);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  const withPassword = async () => {
    setBusy(true);
    try {
      if (!challenge) {
        const start = await api.initiateAuth(username, "PASSWORD", password);
        if (start.AuthenticationResult) return await finish(start);
        if (start.ChallengeName === "SOFTWARE_TOKEN_MFA" && start.Session) {
          setChallenge({ name: start.ChallengeName, session: start.Session });
          return;
        }
        throw new Error(`Cognito asked for ${start.ChallengeName ?? "something this page cannot answer"}; sign out and in again instead.`);
      }
      const result = await api.respondToChallenge(challenge.name, challenge.session, { USERNAME: username, SOFTWARE_TOKEN_MFA_CODE: code });
      await finish(result);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog title="Confirm it's you" onClose={() => onDone(false)}>
      <p className="text-muted">This changes how you sign in, and your last sign-in was more than fifteen minutes ago. Prove it's you once and carry on.</p>
      {possible && hasPasskey && (
        <Button variant="primary" className="mt-4 w-full" disabled={busy} onClick={() => void withPasskey()}>
          Use a passkey
        </Button>
      )}
      <form
        className="mt-4 space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          void withPassword();
        }}
      >
        {!challenge ? (
          <TextField label="Password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
        ) : (
          <TextField label="Code from your authenticator app" inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value)} required pattern="[0-9]{6}" />
        )}
        <Button type="submit" disabled={busy}>
          {challenge ? "Confirm code" : "Continue with password"}
        </Button>
      </form>
      {error !== undefined && <ErrorState title="That did not work." error={error} />}
    </Dialog>
  );
}
